import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import OwlCarousel from "react-owl-carousel";
import { toast } from "react-toastify";
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  EmailIcon,
} from "react-share";
import { Helmet } from "react-helmet-async";

// Import components
import ThumbSlider from "./Sliders/ThumbSlider";
import StarRating from "./Helper/StarRating";
import PopularProductSlider from "./Sliders/PopularProductSlider";
import ReviewSection from "./Reviews/ReviewSection";

// Import actions
import { fetchSingleProducts, fetchProducts } from "./features/productSlice";
import {
  addToCartAPI,
  deleteCartItemApi,
  updateCartQuantityAPI,
} from "./features/cartSlice";
import { toggleWishlistApi } from "./features/wishlistSlice";

// Import assets
import check from "../assets/images/check.svg";

const ProductDetails = () => {
  const { slug } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  // State management
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isIncrementLoading, setIsIncrementLoading] = useState(false);
  const [isDecrementLoading, setIsDecrementLoading] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [localWishlistStatus, setLocalWishlistStatus] = useState(false);
  const [selectedWeight, setSelectedWeight] = useState("");
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  // const [showThumbSlider, setShowThumbSlider] = useState(false);

  // Selectors
  const singleProductData = useSelector((state) => state.product.singleProduct);
  const { popularProducts } = useSelector((state) => state.product);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist?.wishlistItems);

  useEffect(() => {
    // Scroll instantly to top
    window.scrollTo(0, 0);

    // // Show the slider
    // setShowThumbSlider(true);

    // // Hide it after 0.5s
    // const timer = setTimeout(() => {
    //   setShowThumbSlider(false);
    // }, 3500);

    // return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!slug) {
      console.error("No slug found in URL");
      return;
    }

    console.log("Fetching product with slug:", slug); // Debug log

    // Start fetching data immediately
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchSingleProducts(slug)),
          dispatch(fetchProducts()),
        ]);
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };

    fetchData();

    // Ensure the loader is shown for at least 1.2 seconds
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, [dispatch, slug, location.pathname]);

  useEffect(() => {
    const isInWishlist = Object.values(wishlistItems)?.some(
      (item) => item?._id === singleProductData?._id
    );
    setLocalWishlistStatus(isInWishlist);
  }, [wishlistItems, singleProductData?._id]);

  const cartItem = cartItems.find(
    (item) =>
      (item?.productId?._id === singleProductData?._id ||
        item?.productId === singleProductData?._id) &&
      item.weightId.weightInGrams === selectedWeight
  );
  const isInCart = Boolean(cartItem);
  // Find the selected weight object
  const selectedWeightObject = singleProductData?.weights?.find(
    (weight) => weight.weightInGrams === selectedWeight
  );

  // Update handleIncrement for both structures
  const handleIncrement = async () => {
    if (cartItem) {
      const stockLimit = token
        ? cartItem.weightId.stock
        : cartItem.weights.find((w) => w.weightInGrams === selectedWeight)
            ?.stock;
      if (cartItem.quantity >= stockLimit) {
        toast.info(`Sorry, only ${stockLimit} items available in stock`);
        return;
      }

      setIsIncrementLoading(true);
      try {
        await dispatch(
          updateCartQuantityAPI(
            token ? cartItem.productId?._id : cartItem.product_id,
            cartItem.quantity + 1,
            cartItem.weightId?._id
          )
        );
      } finally {
        setIsIncrementLoading(false);
      }
    }
  };

  // Update handleDecrement for both structures
  const handleDecrement = async () => {
    if (cartItem) {
      setIsDecrementLoading(true);
      try {
        if (cartItem.quantity > 1) {
          await dispatch(
            updateCartQuantityAPI(
              token ? cartItem.productId?._id : cartItem.product_id,
              cartItem.quantity - 1,
              cartItem.weightId?._id
            )
          );
        } else {
          await dispatch(
            deleteCartItemApi({
              productId: token ? cartItem.productId?._id : cartItem.product_id,
              weightId: cartItem.weightId?._id,
            })
          );
        }
      } finally {
        setIsDecrementLoading(false);
      }
    }
  };

  // Update handleAddToCart function
  const handleAddToCart = () => {
    // console.log("selectedWeight in handleAddToCart", selectedWeight);
    const selectedWeightObj = singleProductData.weights.find(
      (w) => w.weightInGrams === selectedWeight
    );
    // console.log("selectedWeightObj in new arrival slider", selectedWeightObj);

    if (!selectedWeightObj) {
      toast.error("Selected weight not available");
      return;
    }

    // Check if item already exists in cart
    const existingItem = cartItems.find((item) => {
      return (
        item.productId?._id === singleProductData._id &&
        item.weightId?._id === selectedWeightObj._id
      );
    });
    // console.log("existingItem in new arrival slider", existingItem);

    if (existingItem) {
      toast.info("Item already exists in the cart");
      return;
    }

    setIsAddToCartLoading(true);
    // For logged-in users, dispatch with proper weight object
    if (token) {
      dispatch(addToCartAPI(singleProductData, selectedWeight)).finally(() =>
        setIsAddToCartLoading(false)
      );
      return;
    }

    // For guest users, format product data for cart
    const cartProduct = {
      ...singleProductData,
      selectedWeight: selectedWeightObj,
    };

    dispatch(addToCartAPI(cartProduct)).finally(() =>
      setIsAddToCartLoading(false)
    );
  };

  const handleAddToWishlist = (productId) => {
    try {
      setIsWishlistLoading(true);
      setLocalWishlistStatus(!localWishlistStatus);
      dispatch(toggleWishlistApi(productId));
    } catch (error) {
      setLocalWishlistStatus(localWishlistStatus);
      toast.error("Failed to update wishlist");
    } finally {
      setIsWishlistLoading(false);
    }
  };

  // Share functionality
  const shareUrl = window.location.href;
  const shareTitle =
    singleProductData?.product_name || "Check out this product";
  const shareDescription =
    singleProductData?.sort_description || "Great product from our store";

  const options2 = {
    loop: popularProducts?.length > 5 ? true : false,
    margin: 10,
    nav: false,
    dots: false,
    autoplay: true,
    responsive: {
      0: {
        items: 1,
        nav: false,
      },
      576: {
        items: 2,
        nav: false,
      },
      768: {
        items: 3,
        nav: false,
      },
      992: {
        items: 4,
        nav: false,
      },
      1200: {
        items: 5,
        nav: false,
      },
    },
  };
  const handleWeightSelect = (weightItem) => {
    setSelectedWeight(weightItem.weightInGrams);
    setSelectedPrice(weightItem.prices?.India || 0);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    // Fetch both single product and all products (which includes popular products)
    Promise.all([dispatch(fetchSingleProducts(slug)), dispatch(fetchProducts())]);
  }, [dispatch, slug, location.pathname]);

  useEffect(() => {
    // console.log("wishlistItems in product details", wishlistItems);
    // Only update if both values are available
    if (wishlistItems && singleProductData?._id) {
      const isInWishlist = wishlistItems.some(
        (item) => item?._id === singleProductData?._id
      );

      // Only set state if value actually changed
      if (isInWishlist !== localWishlistStatus) {
        setLocalWishlistStatus(isInWishlist);
      }
    }
  }, [wishlistItems, singleProductData?._id, localWishlistStatus]);

  useEffect(() => {
    if (singleProductData?.weights?.length > 0) {
      const firstWeight = singleProductData.weights[0];
      setSelectedWeight(firstWeight.weightInGrams);
      setSelectedPrice(firstWeight.prices?.India || 0);
    }
  }, [singleProductData?.weights]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showShareOptions &&
        !event.target.closest(".share-btn") &&
        !event.target.closest(".share-options")
      ) {
        setShowShareOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShareOptions]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const renderDescription = (description) => {
    if (!description) return "";

    const isLongDescription = description.length > 200;

    if (!isLongDescription) {
      return (
        <div
          className="about-content"
          dangerouslySetInnerHTML={{
            __html: description,
          }}
        />
      );
    }

    const displayText = showFullDescription
      ? description
      : description.substring(0, 200) + "...";

    return (
      <div className="about-content-wrapper">
        <div
          className="about-content"
          dangerouslySetInnerHTML={{
            __html: showFullDescription ? description : displayText,
          }}
        />
        <button
          onClick={() => setShowFullDescription(!showFullDescription)}
          className="view-more-btn"
          style={{
            background: "none",
            border: "none",
            color: "#4A148C",
            cursor: "pointer",
            fontWeight: "500",
            padding: "5px 0",
            textDecoration: "underline",
            marginTop: "8px",
          }}
        >
          {showFullDescription ? "View Less" : "View More"}
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="loader-main" style={{ background: "white" }}>
        <div className="loaderaa"></div>
      </div>
    );
  }

  if (!singleProductData) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{`${
          singleProductData?.productName || "Product"
        } - Sweetz`}</title>
        <meta
          name="description"
          content={
            singleProductData?.headerDescription || "View product details"
          }
        />
        <meta
          name="keywords"
          content={`${
            singleProductData?.productName
          }, Indian sweets, traditional sweets, ${
            singleProductData?.category || ""
          }`}
        />
        <meta
          property="og:title"
          content={`${singleProductData?.productName || "Product"} - Sweetz`}
        />
        <meta
          property="og:description"
          content={
            singleProductData?.headerDescription || "View product details"
          }
        />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        {singleProductData?.productImages?.[0] && (
          <>
            <meta
              property="og:image"
              content={singleProductData.productImages[0]}
            />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta
              property="og:image:alt"
              content={singleProductData.productName}
            />
          </>
        )}
        <meta property="product:price:amount" content={selectedPrice || ""} />
        <meta property="product:price:currency" content="INR" />
        <meta property="og:site_name" content="Sweetz" />
        {/* Product Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: singleProductData?.productName || "",
            image: singleProductData?.productImages?.[0] || "",
            description: singleProductData?.headerDescription || "",
            brand: {
              "@type": "Brand",
              name: "Sweetz",
            },
            offers: {
              "@type": "Offer",
              url: window.location.href,
              priceCurrency: "INR",
              price: selectedPrice || 0,
              availability:
                singleProductData?.stock > 0
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
            },
            aggregateRating: singleProductData?.averageRating
              ? {
                  "@type": "AggregateRating",
                  ratingValue: singleProductData.averageRating,
                  reviewCount: singleProductData.reviewCount || 0,
                }
              : undefined,
          })}
        </script>
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <style>
        {`
          /* Product transitions and hover effects */
          .product-detail-content {
            transition: all 0.3s ease;
          }
          
          .product-detail-content h4 {
            position: relative;
            display: inline-block;
          }
          
          .product-detail-content h4:after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -4px;
            left: 0;
            background-color: #3c3260;
            transition: width 0.3s ease;
          }
          
          .product-detail-content h4:hover:after {
            width: 100%;
          }

          /* Quantity button effects */
          .quantity-btn button {
            transition: all 0.3s ease;
            transform: scale(1);
          }
          
          .quantity-btn button:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 8px rgba(91, 75, 136, 0.2);
          }
          
          .quantity-btn button.select {
            animation: pulse 1s;
          }

          /* Cart and wishlist button effects */
          .thm-btn, .fav-btn, .share-new-btn {
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .thm-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(91, 75, 136, 0.3);
          }

          .fav-btn:hover, .share-new-btn:hover {
            transform: scale(1.1);
          }

          .fav-btn i.fas.fa-heart {
            color: #ff4b4b;
            animation: heartBeat 0.3s ease;
          }

          /* Share options animation */
          .share-options {
            animation: slideIn 0.3s ease;
          }

          /* Price tag effects */
          .price .discounted-price {
            position: relative;
            display: inline-block;
          }

          .price .discounted-price:before {
            content: '';
            position: absolute;
            height: 2px;
            width: 100%;
            background-color: #3c3260;
            bottom: -2px;
            left: 0;
            transform: scaleX(0);
            transition: transform 0.3s ease;
          }

          .price:hover .discounted-price:before {
            transform: scaleX(1);
          }

          /* Animations */
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }

          @keyframes heartBeat {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Tab styling improvements */
          .product-details-tabs .nav {
            border-bottom: 2px solid #eee;
          }

          .product-details-tabs .tab-link {
            position: relative;
            transition: all 0.3s ease;
          }

          .product-details-tabs .tab-link:after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 100%;
            height: 2px;
            background: #3c3260;
            transform: scaleX(0);
            transition: transform 0.3s ease;
          }

          .product-details-tabs .tab-link.active:after {
            transform: scaleX(1);
          }

          /* Review section improvements */
          .tab-pane {
            transition: opacity 0.3s ease;
          }

          .tab-pane.fade {
            opacity: 0;
          }

          .tab-pane.show {
            opacity: 1;
          }
        `}
      </style>
      <section className="product-detail tp-space mt-150">
        <div className="container">
          <div className="row justify-content-around">
            {singleProductData &&
              singleProductData?.productImages?.length > 0 && (
                <ThumbSlider
                  key={singleProductData._id}
                  singleProductData={singleProductData?.productImages}
                  discount_percentage={singleProductData?.discount_percentage}
                />
              )}
            <div className="col-lg-5">
              <div className="product-detail-content">
                <p className="auth">
                  {singleProductData?.authentic && (
                    <span className="d-flex">
                      <img src={check} className="me-1" alt="check" />
                      Authentic{" "}
                    </span>
                  )}
                  {singleProductData?.stock && (
                    <span className="text-success">
                      <i className="fa fa-check me-1" />
                      In stock
                    </span>
                  )}
                </p>
                <h4 style={{ color: "#3c3260" }}>
                  {singleProductData?.productName}
                </h4>
                <p
                  className="mb-4"
                  dangerouslySetInnerHTML={{
                    __html: singleProductData?.headerDescription,
                  }}
                />
                <p className="rating">
                  <StarRating rating={singleProductData?.averageRating} />(
                  <span
                    onClick={() => {
                      document
                        .querySelector('[data-bs-target="#tab-03"]')
                        .click();
                      document.getElementById("tab-03").scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }}
                    style={{
                      cursor: "pointer",
                      textDecoration: "none",
                      // color: "#3c3260", // Bootstrap primary color
                      color: "#3c3260",
                      fontWeight: "bold",
                      transition:
                        "color 0.3s ease, text-decoration 0.3s ease, opacity 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = "underline";
                      e.target.style.opacity = "0.8";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = "none";
                      e.target.style.opacity = "1";
                    }}
                  >
                    {singleProductData?.reviewCount}{" "}
                    {singleProductData?.reviewCount === 1
                      ? "review"
                      : "reviews"}
                  </span>
                  )
                </p>
                <p className="price ">
                  {singleProductData?.discount_percentage > 0 ? (
                    <>
                      <span className="discounted-price m-2">
                        ₹{" "}
                        {selectedPrice
                          ? (
                              selectedPrice -
                              (selectedPrice *
                                singleProductData?.discount_percentage) /
                                100
                            ).toFixed(2)
                          : "0.00"}
                      </span>
                      <del className="ps-2">
                        ₹ {selectedPrice ? selectedPrice.toFixed(2) : "0.00"}
                      </del>
                    </>
                  ) : (
                    <span className="regular-price">
                      ₹ {selectedPrice ? selectedPrice.toFixed(2) : "0.00"}
                    </span>
                  )}
                </p>
                <h6 className="fz-16 text-dark fw-400">Available in:</h6>
                <div className="quantity-btn">
                  {singleProductData?.weights?.map((item, index) => (
                    <button
                      key={item._id}
                      type="button"
                      className={
                        selectedWeight === item.weightInGrams ? "select" : ""
                      }
                      onClick={() => handleWeightSelect(item)}
                    >
                      {item.weightInGrams}g
                    </button>
                  ))}
                </div>
                <div className="quantity-main position-relative">
                  {!isInCart ? (
                    selectedWeightObject?.stock > 0 ? (
                      <button
                        onClick={handleAddToCart}
                        className="thm-btn"
                        disabled={
                          !selectedWeightObject?.stock || isAddToCartLoading
                        }
                      >
                        {isAddToCartLoading ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <i className="fas fa-shopping-basket me-1" />
                        )}
                        Add to Cart
                      </button>
                    ) : (
                      <button
                        className="thm-btn secondary"
                        data-bs-toggle="modal"
                        data-bs-target="#notify-modal"
                        data-product-id={singleProductData?._id}
                      >
                        <i className="fas fa-envelope me-2"></i>Notify
                      </button>
                    )
                  ) : (
                    <div className="quantity-controls d-flex align-items-center">
                      <button
                        onClick={handleDecrement}
                        className="quantity-controls-btn"
                        disabled={isDecrementLoading}
                      >
                        {isDecrementLoading ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          "-"
                        )}
                      </button>
                      <span className="mx-2">{cartItem?.quantity || 0}</span>
                      <button
                        onClick={handleIncrement}
                        className="quantity-controls-btn"
                      >
                        {isIncrementLoading ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          "+"
                        )}
                      </button>
                    </div>
                  )}
                  <button
                    className="fav-btn"
                    onClick={() => handleAddToWishlist(singleProductData?._id)}
                  >
                    <i
                      className={`${
                        localWishlistStatus
                          ? "fas fa-heart"
                          : "fa-regular fa-heart"
                      }`}
                    />
                  </button>
                  <button
                    className="share-new-btn"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      setShowShareOptions(!showShareOptions);
                      // console.log("Share button clicked", { showShareOptions }); // Debug log
                    }}
                  >
                    <i className="fas fa-share-alt" />
                  </button>
                  {showShareOptions && (
                    <div
                      className="share-options"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        right: "0",
                        top: "100%",
                        marginTop: "10px",
                        background: "white",
                        padding: "15px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        zIndex: 1000,
                        width: "290px",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 10px 0",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Share this product
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, 1fr)",
                          gap: "10px",
                          marginBottom: "15px",
                        }}
                      >
                        <FacebookShareButton
                          url={shareUrl}
                          quote={shareTitle}
                          // hashtag="#product"
                          className="cursor-pointer"
                          style={{ display: "flex", justifyContent: "center" }}
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event bubbling
                            // console.log("Facebook share clicked");
                          }}
                        >
                          <FacebookIcon size={40} round={true} />
                        </FacebookShareButton>

                        <TwitterShareButton
                          url={shareUrl}
                          title={shareTitle}
                          hashtags={["product"]}
                          className="cursor-pointer"
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <TwitterIcon size={40} round={true} />
                        </TwitterShareButton>

                        <WhatsappShareButton
                          url={encodeURIComponent(shareUrl)}
                          title={encodeURIComponent(shareTitle)}
                          separator="\n\n"
                          className="cursor-pointer"
                          style={{ display: "flex", justifyContent: "center" }}
                          onClick={() =>
                            console.log("WhatsApp share clicked", {
                              url: shareUrl,
                              title: shareTitle,
                            })
                          }
                          onShareWindowClose={() =>
                            console.log("WhatsApp share window closed")
                          }
                        >
                          <WhatsappIcon size={40} round={true} />
                        </WhatsappShareButton>

                        <EmailShareButton
                          url={shareUrl}
                          subject={shareTitle}
                          className="cursor-pointer"
                          body={`${shareDescription}\n\nCheck it out: ${shareUrl}`}
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <EmailIcon size={40} round={true} />
                        </EmailShareButton>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 14px",
                          background: "#f5f5f5",
                          borderRadius: "6px",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="text"
                          value={window.location.href}
                          readOnly
                          style={{
                            flex: 1,
                            border: "none",
                            background: "transparent",
                            fontSize: "12px",
                            outline: "none",
                            color: "#666",
                            textAlign: "center",
                          }}
                        />
                        <button
                          onClick={handleCopyLink}
                          style={{
                            border: "none",
                            background: showCopySuccess ? "#3c3260" : "#4b3e78",
                            color: "white",
                            padding: "0px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            transition: "background 0.3s",
                            textAlign: "center",
                            minWidth: "70px",
                          }}
                        >
                          {showCopySuccess ? "Copied!" : "Copy Link"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {singleProductData.stock <= 5 && (
                  <p className="text-success mt-3">
                    Only {singleProductData.stock} left in stock
                  </p>
                )}
              </div>
            </div>
            <div className="col-lg-12">
              <div className="product-details-tabs">
                <ul className="nav">
                  <li className="tab-item">
                    <button
                      className="tab-link active"
                      data-bs-toggle="tab"
                      data-bs-target="#tab-01"
                      type="button"
                      role="tab"
                    >
                      Description
                    </button>
                  </li>
                  <li className="tab-item">
                    <button
                      className="tab-link"
                      data-bs-toggle="tab"
                      data-bs-target="#tab-03"
                      type="button"
                      role="tab"
                    >
                      Reviews
                    </button>
                  </li>
                </ul>
                <div className="tab-content">
                  <div
                    className="tab-pane fade show active"
                    id="tab-01"
                    role="tabpanel"
                  >
                    {renderDescription(singleProductData?.productDescription)}
                  </div>
                  <div className="tab-pane fade" id="tab-03" role="tabpanel">
                    <ReviewSection productId={singleProductData?._id} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="product-sec tp-space">
        <div className="container">
          <div className="heading">
            <h3 className="title">
              Popular <span>Products</span>
            </h3>
            <Link to="/view-all?type=popular" className="custom-btn">
              View More
            </Link>
          </div>
          <OwlCarousel
            className="product-slide owl-theme owl-carousel"
            {...options2}
          >
            {popularProducts &&
              popularProducts?.map((product) => (
                // console.log("product", product),
                <PopularProductSlider
                  key={product._id}
                  product={product}
                  user={user}
                />
              ))}
          </OwlCarousel>
        </div>
      </section>
    </>
  );
};

export default ProductDetails;
